<!-- EntitySelect.vue -->
<template>
  <div class="tw-w-full">
    <o-autocomplete
      v-model="selected"
      :placeholder="placeholder"
      open-on-focus
      :disabled="!isActive"
      :data="sortedResults"
      field="name"
      @typing="searchEntities"
      @select="handleSelect"
    >
      <template slot="empty">
        No results found
      </template>
    </o-autocomplete>
  </div>
</template>

<script>
export default {
  name: 'EntitySelect',
  props: {
    current: {
      type: Object,
      default: null,
    },
    defaultEntities: {
      type: Array,
      default: () => []
    },
    disableApiCalls: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Search for someone'
    },
      isActive: { // NEW: Disable input when needed
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selected: null,
      searchResults: [],
      fetchedEntities: [] // Renamed from defaultEntities to avoid conflict
    };
  },
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    activeResults() {
      // Use either the prop or fetched entities depending on disableApiCalls
      const baseEntities = this.disableApiCalls ? this.defaultEntities : this.fetchedEntities;
      return this.searchResults.length ? this.searchResults : baseEntities;
    },
    sortedResults() {
      const sorted = [...this.activeResults];
      return sorted.sort((a, b) => {
        const names = {
          a: a.name.split(' '),
          b: b.name.split(' '),
        };
        const last = {
          a: names.a[names.a.length - 1],
          b: names.b[names.b.length - 1],
        };
        const first = {
          a: names.a[0].length > 1 ? names.a[0] : null,
          b: names.b[0].length > 1 ? names.b[0] : null,
        };

        const lastComparison = last.a.localeCompare(last.b);
        if (lastComparison !== 0) return lastComparison;

        if (!first.a || !first.b) return 0;
        return first.a.localeCompare(first.b);
      });
    },
  },
  created() {
    this.getDefaultEntities();
  },
  mounted() {
    if (this.current) {
      this.selected = this.current.name;
    }
  },
  watch: {
    current: {
      handler(newVal) {
        this.selected = newVal ? newVal.name : null;
      },
      immediate: true, // Run on initial mount
    },
  },
  methods: {
    handleSelect(option) {
      this.$emit('select', option);
    },
    async getEntities(query=null) {
      let url = '/api/v1/entity/?entity_types=Prospect,Client';
      if (query) url = `${url}&search=${encodeURIComponent(query)}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      });
      const data = await res.json();
      return data.entities;
    },
    async searchEntities(query) {
      // Entity groups passes in disableApiCalls AND the list of possible results. 
      // LinkEntityToMeeting doesn't pass in possible results, so we need to get them with an api call.
      if (this.disableApiCalls) {
        // Filter the defaultEntities prop instead of making API calls
        if (!query) {
          this.searchResults = [];
          return;
        }
        this.searchResults = this.defaultEntities.filter(entity => 
          entity.name.toLowerCase().includes(query.toLowerCase()) ||
          (entity.email && entity.email.toLowerCase().includes(query.toLowerCase()))
        );
        return;
      }

      // Original API call logic
      if (!query) {
        this.searchResults = [];
        return;
      }
      this.searchResults = await this.getEntities(query);
    },
    async getDefaultEntities() {
      if (this.disableApiCalls) {
        // Don't need to do anything since we're using the prop
        return;
      }
      // Original default entities fetching logic
      this.fetchedEntities = await this.getEntities();
    }
  },
};
</script>
